import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { BUSINESS, ASSETS } from "../lib/assets";
import { FlyingPigSVG } from "./FlyingPig";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../lib/CartContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu & Order" },
  { to: "/seasonings", label: "Seasonings & Merch" },
  { to: "/catering", label: "Catering & Weddings" },
  { to: "/events", label: "Awards & Events" },
  { to: "/small-town-showdown", label: "Small Town Showdown", accent: true },
  { to: "/locations", label: "Locations" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totals, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b border-ink bg-white ${scrolled ? "shadow-hard" : ""}`}
      data-testid="site-header"
    >
      <div className="relative z-10 mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 md:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img
            src={ASSETS.logo}
            alt="When Pigs Fly Family BBQ"
            className="mono-img h-12 w-auto md:h-14"
          />
          <div className="hidden md:block leading-none">
            <div className="font-alfa text-base uppercase tracking-tight">When Pigs Fly</div>
            <div className="eyebrow text-ink/70">Real BBQ · Made the Florida Man Way</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `eyebrow relative py-2 transition-[color,background-size] duration-200 ${
                  l.accent
                    ? isActive
                      ? "text-red-700 link-underline"
                      : "text-red-700/90 hover:text-red-700 hover:link-underline"
                    : isActive
                      ? "text-ink link-underline"
                      : "text-ink/70 hover:text-ink hover:link-underline"
                }`
              }
            >
              {l.accent && (
                <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-red-600 align-middle animate-pulse" aria-hidden="true" />
              )}
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCart}
            data-testid="nav-cart-btn"
            aria-label={`Open cart · ${totals.count} items`}
            className="relative flex h-11 w-11 items-center justify-center border border-ink bg-white transition-colors hover:bg-ink hover:text-bone"
          >
            <ShoppingBag size={18} />
            {totals.count > 0 && (
              <span
                data-testid="nav-cart-badge"
                className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center border-2 border-white bg-red-700 px-1 font-mono text-[10px] font-bold leading-none text-bone"
              >
                {totals.count}
              </span>
            )}
          </button>
          <a
            href={BUSINESS.toastOrderUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-order-btn"
            className="hidden md:inline-block border border-ink bg-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-white hover:text-ink"
          >
            Order Online
          </a>
          <button
            data-testid="nav-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            className="lg:hidden relative flex h-14 w-20 flex-col items-center justify-center transition-transform duration-200 hover:scale-105"
            onClick={() => setOpen((o) => !o)}
          >
            <FlyingPigSVG className="piglet-badge absolute inset-x-0 top-0 h-11 w-full" />
            <span className="pointer-events-none absolute -bottom-1 left-0 right-0 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-ink">
              Click Here
            </span>
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-ink bg-white max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain"
          data-testid="nav-mobile"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col px-4 py-2 text-right">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center justify-end gap-3 border-b border-ink/30 py-3 font-mono text-xs uppercase tracking-[0.2em] ${
                    l.accent
                      ? (isActive ? "text-red-700" : "text-red-700/90")
                      : (isActive ? "text-ink" : "text-ink/70")
                  }`
                }
              >
                {l.accent && (
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-600 animate-pulse" aria-hidden="true" />
                )}
                <FlyingPigSVG className="piglet-badge h-6 w-8 shrink-0" />
                <span>{l.label}</span>
              </NavLink>
            ))}
            <a
              href={BUSINESS.toastOrderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-mobile-order-btn"
              className="my-3 border border-ink bg-ink px-4 py-3 text-right font-mono text-xs uppercase tracking-[0.2em] text-white"
            >
              Order Online →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
